import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import { useRefreshOn } from '../../lib/resourceRefresh';
import type { BrandIdentityRead } from '../../types/api';
import { fetchBrandIdentity } from './knowledgeBaseApi';

export interface BrandIdentityResult {
  identity: BrandIdentityRead | null;
  isLoading: boolean;
  error: string | null;
}

/** Result of the last completed load, tagged with the business it belongs to. */
interface LoadedIdentity {
  businessId: string;
  identity: BrandIdentityRead | null;
  error: string | null;
}

type IdentityListener = (identity: BrandIdentityRead) => void;

const identityListeners = new Set<IdentityListener>();

/**
 * Tells every mounted `useBrandIdentity` that the record changed somewhere
 * other than its own screen (a brand suggestion accepted), so none of them
 * keeps showing the old one. Screens mounted later read it fresh anyway.
 */
export function publishBrandIdentity(identity: BrandIdentityRead): void {
  identityListeners.forEach((listener) => listener(identity));
}

/**
 * The brand-identity singleton, read-only.
 *
 * Screens outside the knowledge base consume the brand rather than edit it:
 * the layer editor offers the brand's colours as backgrounds and draws the
 * brand's logo on its preview. Neither is allowed to invent either, so both
 * read from here and show an honest gap when the record is empty.
 */
export function useBrandIdentity(businessId: string | null): BrandIdentityResult {
  const [loaded, setLoaded] = useState<LoadedIdentity | null>(null);
  const [nonce, setNonce] = useState(0);

  // Changed elsewhere without the record in hand (the assistant): re-read it.
  const refresh = useCallback(() => setNonce((value) => value + 1), []);
  useRefreshOn(businessId, 'brand_identity', refresh);

  useEffect(() => {
    if (!businessId) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const identity = await fetchBrandIdentity(businessId, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId, identity, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded((current) =>
          // A failed re-read keeps the record already on screen.
          current?.businessId === businessId && current.identity !== null
            ? current
            : {
                businessId,
                identity: null,
                error: getErrorMessage(caught, 'Could not load your brand identity.'),
              },
        );
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, nonce]);

  useEffect(() => {
    if (!businessId) return;
    const listener: IdentityListener = (identity) => {
      if (identity.business_id !== businessId) return;
      setLoaded({ businessId, identity, error: null });
    };
    identityListeners.add(listener);
    return () => {
      identityListeners.delete(listener);
    };
  }, [businessId]);

  const isCurrent = loaded !== null && loaded.businessId === businessId;

  return {
    identity: isCurrent ? loaded.identity : null,
    isLoading: businessId !== null && !isCurrent,
    error: isCurrent ? loaded.error : null,
  };
}
